import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Briefcase, X, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal, Stagger, StaggerItem, SectionShell } from "../components/Reveal";
import { CtaSection } from "../components/CtaSection";
import { CAREERS_ROLES } from "../content";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function Careers() {
  const [applyRole, setApplyRole] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/careers`, { ...form, role: applyRole.title });
      toast.success("Application sent — our HR team will be in touch.");
      setApplyRole(null);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send right now — please email contact@destinycs.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="careers-page">
      <PageHero
        overline="CAREERS"
        title="Join DestinyCS."
        highlight="Map the world with us."
        desc="A remarkably diverse workforce with unique skill sets and domain expertise — and we're growing. Find your role in the geospatial value chain."
      />

      <SectionShell className="pt-4">
        <div className="flex items-center justify-between mb-10">
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Open roles</h2>
          <span className="font-mono-tech text-[11px] tracking-[0.2em] text-slate-500 uppercase">All departments</span>
        </div>
        <Stagger className="space-y-4">
          {CAREERS_ROLES.map((r) => (
            <StaggerItem key={r.id}>
              <div
                className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 border border-white/10 rounded-2xl bg-[#111622]/70 px-7 py-6 hover:border-cyan-500/50 hover:-translate-y-0.5 transition-all duration-300"
                data-testid={`role-card-${r.id}`}
              >
                <div>
                  <h3 className="font-display text-lg sm:text-xl font-bold text-white group-hover:text-cyan-300 transition-colors">{r.title}</h3>
                  <div className="mt-2 flex flex-wrap gap-4 font-mono-tech text-[11px] text-slate-500 uppercase tracking-wider">
                    <span className="flex items-center gap-1.5"><Briefcase size={12} className="text-cyan-500" />{r.dept}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={12} className="text-cyan-500" />{r.loc}</span>
                    <span>{r.type}</span>
                  </div>
                </div>
                <button
                  onClick={() => setApplyRole(r)}
                  data-testid={`apply-button-${r.id}`}
                  className="shrink-0 flex items-center gap-2 border border-cyan-400/40 text-cyan-300 text-sm font-semibold px-6 py-2.5 rounded-full hover:bg-cyan-400 hover:text-[#05070A] transition-all duration-300"
                >
                  Apply <ArrowUpRight size={14} />
                </button>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </SectionShell>

      <AnimatePresence>
        {applyRole && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#05070A]/80 backdrop-blur-md"
            onClick={() => setApplyRole(null)}
            data-testid="apply-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#0B0E14] border border-white/15 rounded-3xl p-8"
              data-testid="apply-modal"
            >
              <button onClick={() => setApplyRole(null)} data-testid="apply-modal-close" className="absolute top-5 right-5 text-slate-500 hover:text-white" aria-label="Close">
                <X size={20} />
              </button>
              <p className="font-mono-tech text-[10px] tracking-[0.25em] text-cyan-400 uppercase">Applying for</p>
              <h3 className="font-display text-2xl font-extrabold text-white mt-1.5">{applyRole.title}</h3>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input
                  required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="apply-name-input"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60"
                />
                <input
                  required type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="apply-email-input"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60"
                />
                <textarea
                  rows={3} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="A note about you (optional)"
                  data-testid="apply-message-input"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60 resize-none"
                />
                <button
                  type="submit" disabled={sending}
                  data-testid="apply-submit-button"
                  className="w-full bg-cyan-400 text-[#05070A] font-semibold py-3.5 rounded-full hover:bg-cyan-300 transition-all disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Submit application"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaSection />
    </main>
  );
}
